import { useCallback, useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Notice } from '@tcrd/ui';
import { GoogleButton } from '../../components/GoogleButton';
import { errorMessage } from '../../lib/api';
import { clearDriverCode, linkStoredDriver, storedDriverCode } from '../../lib/driver-invite';
import { homeFor, useAuth } from '../../stores/auth';
import type { AuthUser } from '@tcrd/shared';

/** Solo cuentas locales (correo + contraseña) o Google. */
export function parseEmail(raw: string): string | null {
  const v = raw.trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? v : null;
}

/** Estructura común de las pantallas de acceso (barra negra con el logo, como Uber). */
export function AuthShell({ children, back = '/' }: { children: React.ReactNode; back?: string }) {
  return (
    <IonPage>
      <IonContent>
        <div className="min-h-full bg-[var(--surface)] flex flex-col">
          <header className="bg-black text-white pt-[env(safe-area-inset-top)]">
            <div className="mx-auto max-w-md px-6 h-16 flex items-center">
              <Link to={back} aria-label="Inicio" className="text-2xl font-semibold tracking-tight">
                TuConductor<span className="text-brand-red">RD</span>
              </Link>
            </div>
          </header>
          <main className="mx-auto w-full max-w-md px-5 pt-8 pb-12 flex flex-col gap-5">{children}</main>
        </div>
      </IonContent>
    </IonPage>
  );
}

export function LoginPage() {
  const login = useAuth((s) => s.login);
  const loginWithGoogle = useAuth((s) => s.loginWithGoogle);
  const history = useHistory();
  const { search } = useLocation();
  const next = new URLSearchParams(search).get('next');
  const [step, setStep] = useState<'id' | 'password'>('id');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const done = useCallback(
    async (user: AuthUser) => {
      await linkStoredDriver(user.roles);
      // Solo se aceptan rutas internas en ?next= (evita redirecciones abiertas).
      const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : null;
      history.replace(safeNext ?? homeFor(user));
    },
    [history, next],
  );

  const onGoogle = useCallback(
    async (credential: string) => {
      setError(null);
      const user = await loginWithGoogle(credential, storedDriverCode());
      clearDriverCode();
      await done(user);
    },
    [loginWithGoogle, done],
  );
  const onGoogleError = useCallback((m: string) => setError(m), []);

  const submitId = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!parseEmail(identifier)) {
      setError('Escribe un correo electrónico válido.');
      return;
    }
    setStep('password');
  };

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = parseEmail(identifier);
    if (!email || !password) return;
    setError(null);
    setBusy(true);
    try {
      await done(await login({ email, password }));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const input = 'w-full h-14 rounded-xl bg-[var(--surface-2)] px-4 text-base outline-none border-2 border-transparent focus:border-[var(--text)] transition-colors';
  const primary = 'tap w-full h-14 rounded-xl bg-black text-white dark:bg-white dark:text-black font-semibold disabled:opacity-50';

  return (
    <AuthShell>
      {error && <Notice tone="danger">{error}</Notice>}

      {step === 'id' ? (
        <>
          <form onSubmit={submitId} className="flex flex-col gap-4" noValidate>
            <label htmlFor="login-id" className="text-xl leading-snug">
              ¿Cuál es tu correo electrónico?
            </label>
            <input
              id="login-id"
              className={input}
              type="email"
              placeholder="Ingresa tu correo electrónico"
              autoComplete="email"
              inputMode="email"
              autoFocus
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
            />
            <button type="submit" className={primary}>
              Continuar
            </button>
          </form>
          <GoogleButton onCredential={onGoogle} onError={onGoogleError} />
          <p className="text-sm text-center">
            ¿No tienes cuenta?{' '}
            <Link to={`/register${search}`} className="font-semibold underline">
              Regístrate
            </Link>
          </p>
          <p className="text-xs text-[var(--text-muted)] leading-relaxed">
            Al continuar aceptas los{' '}
            <Link to="/legal/terminos" className="underline">Términos</Link> y la{' '}
            <Link to="/legal/privacidad" className="underline">Política de privacidad</Link>.
          </p>
        </>
      ) : (
        <form onSubmit={submitPassword} className="flex flex-col gap-4" noValidate>
          <h1 className="text-2xl font-semibold">Escribe tu contraseña</h1>
          <button type="button" className="tap -mt-2 text-left text-sm text-[var(--text-muted)]" onClick={() => (setStep('id'), setPassword(''), setError(null))}>
            {identifier.trim()} · <span className="underline">Cambiar</span>
          </button>
          {/* Campo oculto para que el gestor de contraseñas asocie el usuario. */}
          <input type="text" name="username" autoComplete="username" value={identifier} readOnly hidden />
          <input
            className={input}
            type="password"
            placeholder="Contraseña"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button type="submit" className={primary} disabled={!password || busy}>
            {busy ? 'Entrando…' : 'Continuar'}
          </button>
          <Link to="/forgot-password" className="tap text-sm text-center underline">
            ¿Olvidaste tu contraseña?
          </Link>
        </form>
      )}
    </AuthShell>
  );
}
